import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CreatePresence from "@/pages/CreatePresence";
import Welcome from "@/pages/Welcome";
import PresenceProfile from "@/pages/PresenceProfile";
import PresenceArrives from "@/pages/PresenceArrives";
import Conversation from "@/pages/Conversation";
import FallbackChat from "@/pages/FallbackChat";
import Settings from "@/pages/Settings";
import Upgrade from "@/pages/Upgrade";
import BillingReturn from "@/pages/BillingReturn";
import Inbox from "@/pages/Inbox";
import Legal from "@/pages/Legal";
import ReportAbuse from "@/pages/ReportAbuse";
import CreateMoment from "@/pages/CreateMoment";
import MomentView from "@/pages/MomentView";
import Pricing from "@/pages/Pricing";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import SubscriptionCancel from "@/pages/SubscriptionCancel";
import TermsPage from "@/pages/legal/Terms";
import PrivacyPage from "@/pages/legal/Privacy";
import BiometricConsentPage from "@/pages/legal/BiometricConsent";
import AINoticePage from "@/pages/legal/AINotice";
import MemorialPolicyPage from "@/pages/legal/MemorialPolicy";
import MemorialDispute from "@/pages/MemorialDispute";
import OnboardingConsent from "@/pages/OnboardingConsent";
import PrivacySettings from "@/pages/PrivacySettings";
import AdminDisputes from "@/pages/AdminDisputes";
import LetterReturn from "@/pages/LetterReturn";
import FreeCard from "@/pages/FreeCard";
import AdminAnalytics from "@/pages/AdminAnalytics";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { trackPageView } from "@/lib/track";
import { useEffect } from "react";
import CookieBanner from "@/components/CookieBanner";
import GlobalFooter from "@/components/GlobalFooter";
import GlobalAmbientAudio from "@/components/GlobalAmbientAudio";
import InstallBanner from "@/components/InstallBanner";
import InstallButton from "@/components/InstallButton";
import { LocaleProvider } from "@/lib/locale";
import ProtectedRoute from "@/components/ProtectedRoute";
import ComplianceGate from "@/components/ComplianceGate";
import BrandMark from "@/components/BrandMark";

/**
 * Shortcut: any authenticated route that should also enforce the
 * compliance gate. The gate runs INSIDE the auth wrapper so it can
 * read the session.
 */
const Guarded = ({ children }) => (
    <ProtectedRoute>
        <ComplianceGate>{children}</ComplianceGate>
    </ProtectedRoute>
);

function AppRouter() {
    const location = useLocation();
    // Fire a page_view on every route change. Fire-and-forget — the
    // tracker is silent and never throws.
    useEffect(() => {
        trackPageView();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/signup" element={<AuthPage mode="signup" />} />
            <Route path="/free-card" element={<FreeCard />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />

            {/* Compliance gate itself — needs auth only, not a compliance check */}
            <Route path="/onboarding/consent" element={<ProtectedRoute><OnboardingConsent /></ProtectedRoute>} />

            {/* Normal authenticated app — guarded by compliance */}
            <Route path="/dashboard" element={<Guarded><Dashboard /></Guarded>} />
            <Route path="/welcome" element={<Guarded><Welcome /></Guarded>} />
            <Route path="/create-presence" element={<Guarded><CreatePresence /></Guarded>} />
            <Route path="/presence/new" element={<Guarded><CreatePresence /></Guarded>} />
            <Route path="/presence/:id" element={<Guarded><PresenceProfile /></Guarded>} />
            <Route path="/presence/:id/arrives" element={<Guarded><PresenceArrives /></Guarded>} />
            <Route path="/chat/:id" element={<Guarded><Conversation /></Guarded>} />
            <Route path="/letter-return" element={<Guarded><LetterReturn /></Guarded>} />
            <Route path="/fallback-chat" element={<Guarded><FallbackChat /></Guarded>} />
            <Route path="/settings" element={<Guarded><Settings /></Guarded>} />
            <Route path="/settings/privacy" element={<ProtectedRoute><PrivacySettings /></ProtectedRoute>} />
            <Route path="/admin/disputes" element={<ProtectedRoute><AdminDisputes /></ProtectedRoute>} />
            <Route path="/upgrade" element={<Guarded><Upgrade /></Guarded>} />
            <Route path="/billing/return" element={<Guarded><BillingReturn /></Guarded>} />
            <Route path="/inbox" element={<Guarded><Inbox /></Guarded>} />
            <Route path="/presence/:id/moment" element={<Guarded><CreateMoment /></Guarded>} />
            <Route path="/moment/:id" element={<Guarded><MomentView /></Guarded>} />

            {/* Legal pages — public, no auth, no gate */}
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/biometric" element={<BiometricConsentPage />} />
            <Route path="/legal/ai-notice" element={<AINoticePage />} />
            <Route path="/legal/memorial-policy" element={<MemorialPolicyPage />} />
            <Route path="/legal/memorial-dispute" element={<MemorialDispute />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/legale" element={<Legal />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/legal/report" element={<ReportAbuse />} />

            <Route path="/pricing" element={<Pricing />} />
            <Route path="/subscription/success" element={<Guarded><SubscriptionSuccess /></Guarded>} />
            <Route path="/subscription/cancel" element={<Guarded><SubscriptionCancel /></Guarded>} />
            <Route path="*" element={<Landing />} />
        </Routes>
    );
}

function ConditionalGlobalFooter() {
    const location = useLocation();
    // Hide on immersive / cinematic routes where a footer would break the
    // experience, and on Landing (which keeps its richer custom footer).
    const path = location.pathname || "";
    const hideOn = [
        path === "/",
        path.startsWith("/chat/"),
        path.endsWith("/arrives"),
        path === "/login",
        path === "/signup",
    ];
    if (hideOn.some(Boolean)) return null;
    return <GlobalFooter />;
}

/**
 * Persistent watermark: fixed behind every page, very low opacity.
 * Never intrusive — always present. The brand is always there.
 */
function GlobalWatermark() {
    return (
        <>
            <div
                aria-hidden
                data-testid="brand-watermark"
                className="pointer-events-none fixed z-0 text-ink-gold opacity-[0.05] hidden md:block"
                style={{ bottom: "5vh", right: "5vw" }}
            >
                <BrandMark size={200} glow={false} breathing={false} watermark />
            </div>
            <div
                aria-hidden
                className="pointer-events-none fixed z-0 text-ink-gold opacity-[0.035] hidden lg:block"
                style={{ top: "14vh", left: "4vw" }}
            >
                <BrandMark size={120} glow={false} breathing={false} watermark />
            </div>
        </>
    );
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <LocaleProvider>
                    <AuthProvider>
                        <GlobalWatermark />
                        <GlobalAmbientAudio />
                        <AppRouter />
                        <ConditionalGlobalFooter />
                        <CookieBanner />
                        <InstallBanner />
                        <InstallButton />
                    </AuthProvider>
                </LocaleProvider>
            </BrowserRouter>
        </div>
    );
}
